/**
 * Dashboard API
 *
 * API functions for fetching dashboard data
 */

import { NetworkMethodEnum } from "@/types";
import {
  QuoteAverageResponse,
  QuoteStatsResponse,
  DateRangeType,
  QuoteFollowupResponse,
} from "../types";
import { fetcher } from "@/utils/api/dataverse/fetcher";
import { mutator } from "@/utils/api/dataverse/mutator";

/**
 * Get quote average data from Dataverse API
 */
export const getQuoteAverageApi = async (region?: number): Promise<QuoteAverageResponse> => {
  let url = '/QuoteAverageApi';
  if (region !== undefined && region !== null) {
    url += `?region=${region}`;
  }
  const response = await fetcher(url);
  return response;
};

/**
 * Get quote statistics data from Dataverse API
 */
export const getQuoteStatsApi = async (
  dateRange: DateRangeType = "thisweek",
  region?: number,
): Promise<QuoteStatsResponse> => {
  let url = `/QuoteStats?dateRange=${dateRange}`;
  if (region !== undefined && region !== null) {
    url += `&region=${region}`;
  }
  const response = await fetcher(url);
  return response;
};

/**
 * Get quote follow-ups from Dataverse API
 */
export const getQuoteFollowupsApi = async (
  regionFilter?: string,
): Promise<QuoteFollowupResponse> => {
  let filter = "statecode eq 0";
  if (regionFilter) {
    filter += ` and ${regionFilter}`;
  }
  const response = await fetcher(
    `/QuoteFollowupApi?$filter=${filter}&$expand=dogma_Quote($select=name,quotenumber,totalamount,dogma_region;$expand=customerid_account($select=name,address1_line1,emailaddress1,telephone1), customerid_contact($select=fullname,emailaddress1,telephone1))&$orderby=dogma_followupdate asc`,
  );
  return response;
};

/**
 * Get quote follow-ups for a specific quote
 */
export const getQuoteFollowupsByQuoteId = async (quoteId: string): Promise<QuoteFollowupResponse> => {
  const response = await fetcher(`/QuoteFollowupApi?$filter=statecode eq 0 and _dogma_quote_value eq ${quoteId}&$expand=dogma_Quote($select=name,quotenumber,totalamount)&$orderby=dogma_followupdate asc`);
  return response;
};

/**
 * Create a new quote follow-up
 */
export const createQuoteFollowupApi = async (payload: import('../types').CreateQuoteFollowupPayload): Promise<import('../types').QuoteFollowup> => {
  return await mutator(NetworkMethodEnum.POST, `/QuoteFollowupApi`, payload);
};

/**
 * Update quote follow-up status
 */
export const updateQuoteFollowupApi = async (payload: import('../types').UpdateQuoteFollowupPayload): Promise<import('../types').QuoteFollowup> => {
  const { ...updateData } = payload;
  const response = await mutator(
    NetworkMethodEnum.PATCH,
    "QuoteFollowupApi",
    updateData,
  );
  return response;
};
